import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Repositories } from '@core/db';
import { OPEN_CASE_STATUSES, type Case, type CaseEvent } from '@core/domain/model';
import { entriesFor, isOverdue } from './grouping';
import type { TasksState, TasksTab } from './types';
import { telemetry } from '@core/telemetry';

/**
 * Данные экрана «Задачи».
 *
 * Задача и событие календаря — одна сущность ([DATA_MODEL.md](docs/DATA_MODEL.md)):
 * «Задачи» и «Календарь» это два взгляда на неё, а не два хранилища.
 */
export function useTasks(db: Repositories, now: () => number = Date.now) {
  const [tab, setTab] = useState<TasksTab>('today');
  const [events, setEvents] = useState<CaseEvent[]>([]);
  const [cases, setCases] = useState<Case[]>([]);
  const [isLoading, setLoading] = useState(true);
  const [version, setVersion] = useState(0);

  const refresh = useCallback(() => setVersion((value) => value + 1), []);

  useEffect(() => {
    let dropped = false;

    void (async () => {
      const [allEvents, allCases] = await Promise.all([db.events.all(), db.cases.list({})]);
      if (dropped) return;

      setEvents(allEvents);
      setCases(allCases);
      setLoading(false);
    })();

    return () => {
      dropped = true;
    };
  }, [db, version]);

  const state: TasksState = useMemo(() => {
    const moment = now();
    const titles = new Map(cases.map((item) => [item.id, item.title]));

    return {
      isLoading,
      tab,
      entries: entriesFor(tab, events, titles, moment),
      // Задачу ставят по делу, которое в работе: завершённое дело в списке выбора
      // только удлиняет его.
      cases: cases
        .filter((item) => OPEN_CASE_STATUSES.includes(item.status))
        .map((item) => ({ id: item.id, title: item.title })),
      overdueCount: events.filter((event) => isOverdue(event, moment)).length,
    };
  }, [tab, events, cases, isLoading, now]);

  const actions = useMemo(
    () => ({
      setTab,

      async setCompleted(id: string, isCompleted: boolean) {
        // Считаем только выполнение: снятие галочки — это исправление промаха,
        // а не работа, и в цифре «сделано задач» ему не место.
        if (isCompleted) telemetry.record('taskCompleted');
        await db.events.setCompleted(id, isCompleted);
        refresh();
      },

      /**
       * Перенос срока прямо из списка — свайп влево ([D-34](docs/DECISIONS.md)).
       *
       * Сроки двигаются постоянно, и ради этого не должно приходиться заходить
       * в карточку. `startAt` держим на дедлайне, чтобы задача не разъехалась
       * с календарём.
       */
      async setDeadline(id: string, deadline: number) {
        const current = await db.events.find(id);
        if (!current) return;

        await db.events.update({ ...current, startAt: deadline, deadlineAt: deadline });
        refresh();
      },

      /**
       * Создание задачи по названию, сроку и — если юрист захочет — делу.
       *
       * Дело необязательно: приложение не должно мешать поставить себе напоминание,
       * не относящееся к делам.
       */
      async createTask(title: string, deadline: number, caseId: string | null) {
        const trimmed = title.trim();
        if (trimmed.length === 0) return;

        await db.events.create({
          id: '',
          title: trimmed,
          type: 'Task',
          startAt: deadline,
          endAt: null,
          description: null,
          caseId,
          clientId: null,
          reminderMinutesBefore: null,
          isCompleted: false,
          deadlineAt: deadline,
        });
        telemetry.record('taskCreated');
        refresh();
      },

      refresh,
    }),
    [db, refresh],
  );

  return { state, actions };
}

export type TasksActions = ReturnType<typeof useTasks>['actions'];
